<div class="upload-docs-wrapper" [class.has-viewer]="viewerPage">

  <div class="upload-docs-container">

    <!-- Back button -->
    <button class="btn-back" (click)="goBack()">&larr; {{ 'uploadDocs.back' | translate }}</button>

    <!-- Title -->
    <h2 class="page-title">
      {{ 'uploadDocs.title' | translate }}
      <span *ngIf="dossierName" class="page-title-sub">
        — {{ dossierName }}<span *ngIf="uploadNumber > 0"> · Upload {{ uploadNumber }}</span>
      </span>
    </h2>

    <!-- Loading -->
    <div class="loading" *ngIf="loading">{{ 'dossier.loading' | translate }}</div>

    <!-- Error -->
    <div class="error-message" *ngIf="error">{{ error }}</div>

    <!-- Processing indicator -->
    <div class="processing-banner" *ngIf="isProcessing && !loading">
      <div class="processing-spinner"></div>
      <span>{{ 'uploadDocs.processing' | translate }}: {{ processingStageLabel }}</span>
    </div>

    <!-- Failed status banner -->
    <div class="error-banner" *ngIf="isFailedStatus && !loading">
      <span>{{ 'uploadDocs.processingFailed' | translate }}: {{ uploadStatus }}</span>
    </div>

    <!-- Batch mode info banner -->
    <div class="lock-banner" *ngIf="batchMode && isClassificationCompleted && !loading">
      <span>{{ 'uploadDocs.batchModeInfo' | translate }}</span>
    </div>

    <!-- Extraction in progress lock banner -->
    <div class="lock-banner" *ngIf="operationsLocked && !batchMode && !loading">
      <div class="processing-spinner"></div>
      <span>{{ 'uploadDocs.extractionInProgress' | translate }}</span>
    </div>

    <!-- Extraction config bar (backend + model selection) -->
    <div class="extraction-config-bar" *ngIf="isClassificationCompleted && !loading">
      <ng-container *ngIf="authService.isDeveloper">
        <label>{{ 'uploadDocs.backend' | translate }}</label>
        <select [(ngModel)]="extractBackend" (ngModelChange)="onExtractBackendChange()">
          <option value="">{{ 'uploadDocs.backendAll' | translate }}</option>
          <option value="openai">{{ 'uploadDocs.backendOpenai' | translate }}</option>
          <option value="bedrock">{{ 'uploadDocs.backendBedrock' | translate }}</option>
          <option value="ollama">{{ 'uploadDocs.backendOllama' | translate }}</option>
          <option value="local">{{ 'uploadDocs.backendLocal' | translate }}</option>
        </select>

        <label>{{ 'uploadDocs.model' | translate }}</label>
        <input list="upload-model-options" [(ngModel)]="extractModel" (ngModelChange)="onExtractModelChange()" placeholder="{{ 'uploadDocs.selectModel' | translate }}">
        <datalist id="upload-model-options">
          <option *ngFor="let m of filteredExtractModels" [value]="m">{{ displayModel(m) }}</option>
        </datalist>
      </ng-container>

      <!-- Model tier slider for non-developer users -->
      <ng-container *ngIf="!authService.isDeveloper && modelTiers.length > 0">
        <label class="tier-label-inline">{{ 'uploadDocs.quality' | translate }}</label>
        <app-model-tier-slider
          [tiers]="modelTiers"
          [selectedModel]="extractModel"
          (modelChange)="onTierModelChange($event)">
        </app-model-tier-slider>
      </ng-container>

      <!-- Output format selector (honoured before extraction, no re-extract needed) -->
      <label>{{ 'uploadDocs.outputFormat' | translate }}</label>
      <select [(ngModel)]="outputFormat" (ngModelChange)="onOutputFormatChange()">
        <option *ngFor="let f of outputFormats" [value]="f">{{ f | uppercase }}</option>
      </select>

      <button class="btn-select-failed" *ngIf="hasFailedDocs" (click)="selectAllFailed()"
              [title]="'uploadDocs.selectFailed' | translate">{{ 'uploadDocs.selectFailed' | translate }}</button>
      <button class="btn-clear-selection" *ngIf="hasSelection" (click)="clearSelection()">{{ 'uploadDocs.clearSelection' | translate }} ({{ selectedDocs.size }})</button>

      <!-- Spacer to push bulk actions to the right -->
      <span class="config-bar-spacer"></span>

      <!-- Bulk selection controls -->
      <button class="btn-toolbar" (click)="selectAll()"
              [title]="'uploadDocs.selectAll' | translate">{{ 'uploadDocs.selectAll' | translate }}</button>
      <button class="btn-toolbar" (click)="selectNone()" *ngIf="hasSelection"
              [title]="'uploadDocs.selectNone' | translate">{{ 'uploadDocs.selectNone' | translate }}</button>

      <!-- Bulk actions -->
      <button class="btn-toolbar btn-toolbar-download"
              (click)="downloadSelectedResults()"
              [title]="'uploadDocs.downloadResults' | translate">&#11015; {{ 'uploadDocs.downloadResults' | translate }}</button>
      <button class="btn-toolbar btn-toolbar-download"
              (click)="downloadSelectedOcrText()"
              [title]="'uploadDocs.downloadOcrText' | translate">&#11015; {{ 'uploadDocs.downloadOcrText' | translate }}</button>
      <button class="btn-toolbar btn-toolbar-reviewed"
              (click)="markSelectedReviewed()"
              [title]="'uploadDocs.markSelectedReviewed' | translate">&#10003; {{ 'uploadDocs.markSelectedReviewed' | translate }}</button>
      <button class="btn-toolbar btn-toolbar-extract"
              (click)="extractSelected()"
              [disabled]="!canExtractSelected"
              [title]="'uploadDocs.extractSelected' | translate">&#9654; {{ 'uploadDocs.extractSelected' | translate }}</button>
    </div>

    <!-- Documents list -->
    <ng-container *ngIf="!loading && documents.length > 0">
      <ng-container *ngFor="let doc of documents; let i = index; let last = last">
        <div class="document-card"
             [class.extracting]="docExtractionStatus[doc.oid] === 'extracting'"
             [class.selected]="isDocSelected(doc)">

        <!-- Card header -->
        <div class="card-header">
          <div class="card-header-left">
            <!-- Selection checkbox -->
            <input type="checkbox"
                   class="doc-checkbox"
                   [checked]="isDocSelected(doc)"
                   (change)="toggleDocSelection(doc)"
                   [disabled]="operationsLocked"
                   aria-label="Select document for re-extraction" />

            <span class="doc-index">{{ i + 1 }}.</span>

            <!-- Schema source selector -->
            <select
              class="schema-dropdown"
              [ngModel]="getSchemaSource(doc)"
              (ngModelChange)="onSchemaSourceChange(doc, $event)"
              [disabled]="!isClassificationCompleted || operationsLocked"
            >
              <option value="internal">{{ 'schemaSourceOptions.internal' | translate }}</option>
              <option value="external">{{ 'schemaSourceOptions.external' | translate }}</option>
            </select>

            <!-- Document type dropdown -->
            <select
              class="type-dropdown"
              [ngModel]="bareDocumentType(doc)"
              (ngModelChange)="onTypeChange(doc, $event)"
              [disabled]="!isClassificationCompleted || operationsLocked"
            >
              <option *ngFor="let t of getDocumentTypesForDoc(doc)" [value]="t">{{ t }}</option>
            </select>

            <span class="page-count">{{ doc.page_count }} {{ 'uploadDocs.pages' | translate }}</span>

            <span class="confidence-badge" *ngIf="doc.confidence != null"
                  [class.green]="doc.confidence > 0.95"
                  [class.light-green]="doc.confidence > 0.90 && doc.confidence <= 0.95"
                  [class.yellow]="doc.confidence > 0.85 && doc.confidence <= 0.90"
                  [class.orange]="doc.confidence >= 0.80 && doc.confidence <= 0.85"
                  [class.red]="doc.confidence < 0.80">
              {{ doc.confidence * 100 | localNumber:'1.0-0' }}%
            </span>

            <!-- Per-document extraction status badge -->
            <span class="status-badge" *ngIf="docExtractionStatus[doc.oid]"
                  [attr.data-status]="docExtractionStatus[doc.oid]">
              {{ getDocStatusLabel(doc) }}
            </span>

            <!-- Model badge (developer only) -->
            <span class="model-badge" *ngIf="authService.isDeveloper && getDocModel(doc) as model">
              {{ displayModel(model) }}
            </span>

            <!-- Tokens badge -->
            <span class="tokens-badge" *ngIf="getDocTokens(doc) as tokens">
              {{ tokens | localNumber:'1.2-2' }} ꝟ
            </span>

            <!-- Cancel extraction -->
            <button class="btn-cancel-extraction"
                    *ngIf="docExtractionStatus[doc.oid] === 'extracting'"
                    title="Cancel extraction"
                    (click)="cancelExtraction(doc)">&#x2715; {{ 'uploadDocs.cancelExtraction' | translate }}</button>

            <!-- Mark Reviewed -->
            <button class="btn-reviewed"
                    *ngIf="canMarkReviewed(doc)"
                    title="Mark as reviewed"
                    (click)="markDocReviewed(doc)">&#10003; {{ 'uploadDocs.markReviewed' | translate }}</button>

            <!-- Download OCR text for this document (available once OCR/classification done) -->
            <button class="btn-reviewed btn-ocr-doc"
                    *ngIf="(documentPages[doc.oid]?.length || 0) > 0"
                    [title]="'uploadDocs.downloadOcrText' | translate"
                    (click)="downloadDocOcrText(doc)">&#11015; {{ 'uploadDocs.downloadOcrText' | translate }}</button>

            <!-- Re-extract single document -->
            <button class="btn-reviewed btn-extract-doc"
                    *ngIf="canExtractDoc(doc)"
                    [disabled]="operationsLocked || extracting"
                    title="Re-extract this document"
                    (click)="extractSingleDoc(doc)">&#9654; {{ 'uploadDocs.extract' | translate }}</button>

            <!-- Send single document -->
            <button class="btn-reviewed btn-send-doc"
                    [disabled]="exportingDocId === doc.oid"
                    [title]="'Send to ' + (downstreamSystemName || 'destination')"
                    (click)="exportDoc(doc)">{{ exportingDocId === doc.oid ? '⏳' : '📤' }} Send{{ downstreamSystemName ? ' to ' + downstreamSystemName : '' }}</button>
            <span class="export-msg-inline" *ngIf="exportDocMsg[doc.oid]"
                  [class.ok]="exportDocMsg[doc.oid].startsWith('✓')"
                  [class.err]="exportDocMsg[doc.oid].startsWith('✗')">{{ exportDocMsg[doc.oid] }}</span>

            <!-- Rejection flag -->
            <span class="downstream-flag" *ngIf="doc.downstream_status === 'rejected'"
                  [title]="doc.downstream_error || 'Rejected by ' + (downstreamSystemName || 'destination')">
              ⚠ {{ doc.downstream_error || 'Rejected by ' + (downstreamSystemName || 'destination') }}
            </span>

            <!-- Reviewed indicator -->
            <span class="reviewed-badge" *ngIf="isReviewed(doc)">&#10003; {{ 'uploadDocs.reviewed' | translate }}</span>

            <!-- Originality badge -->
            <span class="original-badge original" *ngIf="doc.is_original === true">Original</span>
            <span class="original-badge copy" *ngIf="doc.is_original === false">Copy</span>
          </div>

          <div class="card-header-right">
            <!-- Ignore document: exclude from extraction/examination (any doc, reversible) -->
            <button
              class="btn-ignore-doc"
              *ngIf="!batchMode && doc.document_type !== 'IGNORE'"
              [disabled]="!canModifyDocuments"
              title="Ignore this document (exclude from extraction/examination; reversible)"
              (click)="ignoreDocument(doc)"
            >&#8856;</button>
            <span class="doc-ignored-tag" *ngIf="doc.document_type === 'IGNORE'">ignored</span>
            <!-- Gated: looks like a document but matched no known type. Not
                 extracted; the user can assign a type above to bring it back in. -->
            <span
              class="doc-unclassified-tag"
              *ngIf="doc.document_type === 'UNCLASSIFIED'"
              [title]="'uploadDocs.unclassifiedHint' | translate"
            >{{ 'uploadDocs.unclassified' | translate }}</span>
            <!-- Delete document (hidden in batch mode) -->
            <button
              class="btn-delete"
              *ngIf="!batchMode"
              [disabled]="isFirstDocument(doc) || !canModifyDocuments"
              [title]="'uploadDocs.deleteDoc' | translate"
              (click)="deleteDocument(doc)"
            >&times;</button>
          </div>
        </div>

        <!-- Extraction error detail -->
        <div class="extraction-error-detail" *ngIf="docExtractionStatus[doc.oid] === 'extraction_failed' && getExtractionError(doc) as err">
          <span class="extraction-error-icon">&#9888;</span>
          <div class="extraction-error-body">
            <span class="extraction-error-msg">{{ err.error_detail }}</span>
            <span class="extraction-error-hint" *ngIf="err.error_code === 'context_overflow'">
              {{ 'uploadDocs.errorContextOverflowHint' | translate }}
            </span>
            <span class="extraction-error-hint" *ngIf="err.error_code === 'rate_limit'">
              {{ 'uploadDocs.errorRateLimitHint' | translate }}
            </span>
            <span class="extraction-error-hint" *ngIf="err.error_code === 'model_unavailable'">
              {{ 'uploadDocs.errorModelUnavailableHint' | translate }}
            </span>
          </div>
        </div>

        <!-- Thumbnail grid -->
        <div class="thumbnail-grid"
             [class.thumbnail-grid-clickable]="docExtractionStatus[doc.oid] === 'extracted' || docExtractionStatus[doc.oid] === 'reviewed' || docExtractionStatus[doc.oid] === 'extraction_failed'"
             (click)="onThumbnailGridClick($event, doc)"
             (dragover)="onThumbDragOver($event, doc)"
             (drop)="onThumbDrop($event, doc)">

          <!-- Large Review button (overlay, shown when extraction data exists) -->
          <button class="btn-review-large"
                  *ngIf="docExtractionStatus[doc.oid] === 'extracted' || docExtractionStatus[doc.oid] === 'reviewed' || docExtractionStatus[doc.oid] === 'extraction_failed'"
                  title="View extraction detail"
                  (click)="$event.stopPropagation(); openExtractionDetail(doc)">👁 {{ 'uploadDocs.review' | translate }}</button>
          <div class="thumbnail" *ngFor="let page of getPagesForDocument(doc)"
               [attr.draggable]="canModifyDocuments"
               [class.drag-over]="dragOverTarget?.docOid === doc.oid && dragOverTarget?.pageOid === page.oid"
               (dragstart)="onThumbDragStart($event, doc, page)"
               (dragend)="onThumbDragEnd()"
               (dragover)="onThumbDragOverItem($event, doc, page)"
               (dragleave)="onThumbDragLeaveItem()">
            <img
              *ngIf="pageImageUrls[page.oid]"
              [src]="pageImageUrls[page.oid]"
              [alt]="'Page ' + page.page_number"
              class="thumbnail-clickable"
              [class.thumbnail-selected]="viewerPage?.oid === page.oid"
              (click)="openPageInViewer(page)"
            />
            <div class="thumbnail-placeholder" *ngIf="!pageImageUrls[page.oid]">
              {{ page.page_number }}
            </div>
            <span class="thumbnail-label">{{ page.page_number }}</span>

            <!-- Page controls (hidden in batch mode, locked during extraction) -->
            <div class="page-controls" *ngIf="canModifyDocuments">
              <button title="Move first" [disabled]="page.page_number === 1"
                      (click)="movePageFirst(doc, page)">&laquo;</button>
              <button title="Move left" [disabled]="page.page_number === 1"
                      (click)="movePageLeft(doc, page)">&lsaquo;</button>
              <button title="Move right" [disabled]="page.page_number === getPagesForDocument(doc).length"
                      (click)="movePageRight(doc, page)">&rsaquo;</button>
              <button title="Move last" [disabled]="page.page_number === getPagesForDocument(doc).length"
                      (click)="movePageLast(doc, page)">&raquo;</button>
              <button title="Split here" [disabled]="page.page_number === 1"
                      (click)="splitAtPage(doc, page)">&#9986;</button>
              <button title="Move to prev doc" [disabled]="isFirstDocument(doc) || getPagesForDocument(doc).length <= 1"
                      (click)="movePageUp(doc, page)">&uarr;</button>
              <button title="Move to next doc" [disabled]="isLastDocument(doc) || getPagesForDocument(doc).length <= 1"
                      (click)="movePageDown(doc, page)">&darr;</button>
              <button [title]="page.status === 'ignored' ? 'Un-ignore page' : 'Ignore page (exclude from extraction)'"
                      [class.page-ignored-btn]="page.status === 'ignored'"
                      (click)="togglePageIgnored(doc, page)">&#8856;</button>
            </div>
            <span class="thumbnail-ignored-tag" *ngIf="page.status === 'ignored'">ignored</span>
          </div>
          <div class="no-pages" *ngIf="getPagesForDocument(doc).length === 0">
            {{ doc.page_count }} {{ 'uploadDocs.pages' | translate }}
          </div>
        </div>

      </div>

      <!-- Merge connector between documents -->
      <div class="merge-connector" *ngIf="!last && isClassificationCompleted && !batchMode">
        <button
          class="btn-merge-between"
          [disabled]="operationsLocked"
          title="Merge these two documents"
          (click)="mergeWithNext(doc)"
        >⊕</button>
      </div>
      </ng-container>
    </ng-container>

    <!-- Empty state -->
    <div class="empty-state" *ngIf="!loading && documents.length === 0 && !isProcessing">
      {{ 'uploadDocs.noDocuments' | translate }}
    </div>

    <!-- Extract / Next button -->
    <div class="actions-bar" *ngIf="!loading && documents.length > 0">
      <button
        *ngIf="isExtractionDone && !hasSelection"
        class="btn-secondary btn-done"
        (click)="goBack()"
      >
        {{ 'uploadDocs.done' | translate }}
      </button>
      <button
        *ngIf="isClassificationCompleted"
        class="btn-secondary btn-ocr-text"
        [title]="'uploadDocs.downloadOcrText' | translate"
        (click)="hasSelection ? downloadSelectedOcrText() : downloadAllOcrText()"
      >
        &#11015; {{ 'uploadDocs.downloadOcrText' | translate }}
      </button>
      <button
        class="btn-primary btn-extract"
        [disabled]="!canExtract"
        (click)="triggerExtraction()"
      >
        <ng-container *ngIf="extracting">{{ 'uploadDocs.extracting' | translate }}</ng-container>
        <ng-container *ngIf="!extracting && isExtractionDone && hasSelection">{{ 'uploadDocs.reExtractSelected' | translate:{ count: selectedDocs.size } }}</ng-container>
        <ng-container *ngIf="!extracting && (!isExtractionDone || !hasSelection)">{{ 'uploadDocs.extract' | translate }}</ng-container>
      </button>
    </div>

  </div>

  <!-- Side panel page viewer -->
  <div class="viewer-panel" *ngIf="viewerPage" [style.width.px]="viewerPanelWidth">
    <div class="viewer-resize-handle" (mousedown)="onResizeStart($event)"></div>
    <div class="viewer-header">
      <span class="viewer-title">Page {{ viewerPage.page_number }}</span>
      <button class="viewer-close" (click)="closeViewer()">&times;</button>
    </div>
    <div class="viewer-body">
      <img *ngIf="viewerImageUrl" [src]="viewerImageUrl" class="viewer-image" alt="Page preview" />
      <div class="viewer-placeholder" *ngIf="!viewerImageUrl">Loading image...</div>
    </div>
  </div>

</div>